/* eslint-disable @typescript-eslint/no-explicit-any */
import api from './axiosConfig';

// Interfaces
export interface KnowledgeHubCollection {
  id: string;
  name: string;
  description: string;
  vectorStoreName: string;
  documentsCount?: number;
  createdAt: string;
  updatedAt: string;
  vectorStoreInfo?: {
    vectors_count: number;
    points_count: number;
    indexed_vectors_count: number;
    config?: any;
  };
}

export interface KnowledgeHubDocument {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  status: 'pending' | 'processing' | 'indexed' | 'error';
  processingError?: string;
  chunksCount?: number;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeHubSearchResult {
  text: string;
  relevance: number;
  source?: string;
  section?: string;
  document_name?: string;
  page_or_section?: string;
  document_id?: string;
  collection?: string;
  title?: string;
  link?: string;
  snippet?: string;
  displayLink?: string;
  formattedUrl?: string;
  publishedDate?: string;
}

export interface KnowledgeHubSearchResponse {
  status?: string;
  results: KnowledgeHubSearchResult[];
  total_found: number;
  query_understood: boolean;
  processing_time?: number;
  collections_searched?: string[];
  search_type?: 'knowledge' | 'web';
}

export interface KnowledgeHubStats {
  totalCollections: number;
  totalDocuments: number;
  totalVectors: number;
  collections: Array<{
    id: string;
    name: string;
    documentsCount: number;
    vectorsCount: number;
  }>;
  timestamp: string;
}

// API Functions

// Obtener todas las colecciones
export const fetchCollections = async (): Promise<KnowledgeHubCollection[]> => {
  try {
    const response = await api.get<{
      status: string;
      collections: KnowledgeHubCollection[];
    }>('knowledgehub/collections');
    return response.data.collections || [];
  } catch (error) {
    console.error('Error fetching collections:', error);
    throw error;
  }
};

// Crear nueva colección
export const createCollection = async (
  name: string,
  description?: string
): Promise<KnowledgeHubCollection> => {
  try {
    const response = await api.post<any>('knowledgehub/collections', {
      name,
      description
    });
    return response.data.collection || response.data;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

// Obtener detalles de una colección
export const fetchCollectionDetails = async (
  collectionId: string
): Promise<KnowledgeHubCollection> => {
  try {
    const response = await api.get<any>(
      `knowledgehub/collections/${collectionId}`
    );
    return response.data.collection || response.data;
  } catch (error) {
    console.error('Error fetching collection details:', error);
    throw error;
  }
};

// Eliminar colección
export const deleteCollection = async (collectionId: string): Promise<void> => {
  try {
    await api.delete(`knowledgehub/collections/${collectionId}`);
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

// Subir documento a una colección
export const uploadDocument = async (
  collectionId: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<KnowledgeHubDocument> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<any>(
      `knowledgehub/collections/${collectionId}/documents`,
      formData,
      {
        headers: {},
        onUploadProgress
      }
    );
    return response.data.document || response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

// Obtener estado de un documento
export const fetchDocumentStatus = async (
  documentId: string
): Promise<KnowledgeHubDocument> => {
  try {
    const response = await api.get<any>(`knowledgehub/documents/${documentId}`);
    return response.data.document || response.data;
  } catch (error) {
    console.error('Error fetching document status:', error);
    throw error;
  }
};

// Eliminar documento
export const deleteDocument = async (documentId: string): Promise<void> => {
  try {
    await api.delete(`knowledgehub/documents/${documentId}`);
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

// Buscar en la base de conocimientos
export const searchKnowledgeBase = async (params: {
  query: string;
  collection_ids?: string[];
  max_results?: number;
  search_type?: 'knowledge' | 'web';
  language?: string;
}): Promise<KnowledgeHubSearchResponse> => {
  try {
    const response = await api.post<KnowledgeHubSearchResponse>(
      'knowledgehub/search',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error searching knowledge base:', error);
    throw error;
  }
};

// Obtener estadísticas del sistema
export const fetchSystemStats = async (): Promise<KnowledgeHubStats> => {
  try {
    const response = await api.get<KnowledgeHubStats>('knowledgehub/stats');

    // CAMBIO: response.data directamente, no response.data.stats
    return response.data;
  } catch (error) {
    console.error('Error fetching system stats:', error);
    throw error;
  }
};

// Obtener TODOS los documentos
export const fetchAllDocuments = async (): Promise<KnowledgeHubDocument[]> => {
  try {
    const response = await api.get<{
      status: string;
      documents: KnowledgeHubDocument[];
      totalDocuments: number;
    }>('knowledgehub/documents');
    return response.data.documents || [];
  } catch (error) {
    console.error('Error fetching all documents:', error);
    return [];
  }
};

// Obtener documentos de una colección
export const fetchCollectionDocuments = async (
  collectionId: string
): Promise<KnowledgeHubDocument[]> => {
  try {
    const response = await api.get<{
      status: string;
      documents: KnowledgeHubDocument[];
    }>(`knowledgehub/collections/${collectionId}/documents`);
    return response.data.documents || [];
  } catch (error) {
    console.error('Error fetching collection documents:', error);
    return [];
  }
};
